// ============================================================================
// FINANCE DOCUMENT PREVIEW
// Premium read-only preview of financial documents (invoice, quote, bill, etc.)
// Used in WorkItemDetailModal for finance document types
// Matches the quality of DocumentModal's enterprise design
// Date: February 2026
// ============================================================================


'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  Calendar,
  Building2,
  Mail,
  Phone,
  DollarSign,
  Hash,
  Edit,
  ExternalLink,
  AlertTriangle,
  Package,
  Camera,
  Fuel,
  Receipt,
  Wrench,
  Car,
  Link2,
  Send,
  Download,
  Eye,
  Shield,
  Globe,
  MapPin,
  Clock,
  CreditCard,
  Printer,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import UnifiedDocumentPreviewModal, { downloadUnifiedDocumentPdf } from '@/components/reports/UnifiedDocumentPreviewModal';
import { DOCUMENT_TYPE_LABELS, isUnifiedReportDocumentType } from '@/lib/reports';
import { getPurchaseLineSourcingSummary } from '@/lib/finance/purchaseLineSourcing';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

interface LineItem {
  id: string;
  description: string;
  notes?: string;
  quantity: number;
  unit_price: number;
  unit_cost?: number;
  unit?: string;
  tax_rate?: number;
  tax_amount?: number;
  discount_amount?: number;
  line_total: number;
  item_code?: string;
  line_type?: 'item' | 'header' | 'comment' | 'subtotal' | 'spacer';
  is_optional?: boolean;
  is_required?: boolean;
  quantity_editable?: boolean;
  price_editable?: boolean;
  source_bundle_name?: string;
  purchase_supplier_mapping_id?: number;
  purchase_supplier_contact_id?: number;
  purchase_supplier_name?: string | null;
  purchase_supplier_sku?: string | null;
  purchase_supplier_currency?: string | null;
  purchase_supplier_cost_price?: number | null;
  purchase_supplier_is_preferred?: boolean;
  purchase_supplier_priority_rank?: number | null;
  purchase_supplier_lead_time_days?: number | null;
  purchase_supplier_selection_reason?: string | null;
  purchase_supplier_fallback_reason?: string | null;
  purchase_supplier_price_source?: string | null;
  purchase_supplier_matches_document_contact?: boolean;
}

interface FinanceDocument {
  id: string;
  item_type: string;
  doc_number?: string;
  item_number?: string;  // work_items uses item_number
  reference_number?: string;
  title?: string;
  status: string;
  payment_status?: string;
  
  // Contact
  contact_id?: number;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  billing_address?: string;
  shipping_address?: string;
  
  // Dates
  issued_at?: string;
  due_date?: string;
  valid_until?: string;
  expected_delivery_date?: string;
  
  // Financial
  currency?: string;
  exchange_rate?: number;
  subtotal?: number;
  tax_total?: number;
  discount_amount?: number;
  grand_total?: number;
  amount_paid?: number;
  balance_due?: number;
  payment_terms?: string;
  
  // References
  customer_po_number?: string;
  supplier_reference?: string;
  
  // Notes
  notes?: string;
  internal_notes?: string;
  terms_and_conditions?: string;
  
  // Meta
  created_at?: string;
  updated_at?: string;
  
  // Expense source (for bills created from field expenses)
  custom_fields?: {
    expense_source?: {
      expense_id?: string;
      expense_type?: string;
      vehicle_id?: string;
      vehicle_registration?: string;
      captured_by?: string;
      captured_at?: string;
      odometer?: number;
      location?: string;
      receipt_image_url?: string;
    };
    [key: string]: any;
  };
  source_expense_id?: string;
  parent_id?: string;
}

interface FinanceDocumentPreviewProps {
  document: FinanceDocument;
  onEdit?: () => void;
  onOpenFullModal?: () => void;
  onSend?: () => void;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Currency configuration for multi-currency display
const CURRENCY_CONFIG: Record<string, { symbol: string; flag: string }> = {
  ZAR: { symbol: 'R', flag: '\ud83c\uddff\ud83c\udde6' },
  USD: { symbol: '$', flag: '\ud83c\uddfa\ud83c\uddf8' },
  EUR: { symbol: '\u20ac', flag: '\ud83c\uddea\ud83c\uddfa' },
  GBP: { symbol: '\u00a3', flag: '\ud83c\uddec\ud83c\udde7' },
  BWP: { symbol: 'P', flag: '\ud83c\udde7\ud83c\uddfc' },
  NAD: { symbol: 'N$', flag: '\ud83c\uddf3\ud83c\udde6' },
  MZN: { symbol: 'MT', flag: '\ud83c\uddf2\ud83c\uddff' },
  ZMW: { symbol: 'ZK', flag: '\ud83c\uddff\ud83c\uddf2' },
  KES: { symbol: 'KSh', flag: '\ud83c\uddf0\ud83c\uddea' },
  NGN: { symbol: '\u20a6', flag: '\ud83c\uddf3\ud83c\uddec' },
  AUD: { symbol: 'A$', flag: '\ud83c\udde6\ud83c\uddfa' },
  CAD: { symbol: 'C$', flag: '\ud83c\udde8\ud83c\udde6' },
  CHF: { symbol: 'CHF', flag: '\ud83c\udde8\ud83c\udded' },
  INR: { symbol: '\u20b9', flag: '\ud83c\uddee\ud83c\uddf3' },
};

const FINANCE_DOC_TYPES = new Set([
  'invoice', 'quote', 'credit_note', 'debit_note', 'supplier_invoice', 'receipt', 'statement',
  'purchase_order', 'delivery_note', 'waybill', 'sales_order', 'proforma', 'remittance', 'grn',
  'supplier_credit', 'supplier_quote', 'purchase_requisition', 'rfq', 'purchase_return', 'rma'
]);

const PAYMENT_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  paid: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-600 dark:text-emerald-400' },
  partial: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-600 dark:text-yellow-400' },
  unpaid: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' },
  overdue: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
  outstanding: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function isFinanceDocType(itemType: string): boolean {
  return FINANCE_DOC_TYPES.has(itemType);
}

const formatCurrency = (amount: number | string | undefined | null, currency = 'ZAR'): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format(num);
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function FinanceDocumentPreview({ 
  document, 
  onEdit,
  onOpenFullModal,
  onSend
}: FinanceDocumentPreviewProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showUnifiedPreview, setShowUnifiedPreview] = useState(false);
  const unifiedReportDocumentType = isUnifiedReportDocumentType(document.item_type)
    ? document.item_type
    : null;
  const isPurchaseDocument = ['supplier_invoice', 'purchase_order', 'grn', 'supplier_credit', 'supplier_quote', 'purchase_requisition', 'rfq', 'purchase_return'].includes(document.item_type);

  // Download PDF handler
  const handleDownloadPDF = useCallback(async () => {
    if (!document.id || downloadingPDF) return;
    setDownloadingPDF(true);
    try {
      if (unifiedReportDocumentType) {
        await downloadUnifiedDocumentPdf({
          documentId: document.id,
          documentType: unifiedReportDocumentType,
          documentNumber: document.doc_number || document.item_number || document.reference_number || document.id,
        });
        return;
      }

      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');
      const response = await fetch(`/api/v1/documents/${document.id}/pdf?download=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('PDF generation failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      const docNum = document.reference_number || document.title || document.id;
      const typeName = (document.item_type || 'document').replace(/_/g, '-');
      a.download = `${typeName}-${docNum}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloadingPDF(false);
    }
  }, [
    document.doc_number,
    document.id,
    document.item_number,
    document.item_type,
    document.reference_number,
    document.title,
    downloadingPDF,
    unifiedReportDocumentType,
  ]);

  const handleOpenPrintPreview = useCallback(() => {
    if (!unifiedReportDocumentType) return;
    setShowUnifiedPreview(true);
  }, [unifiedReportDocumentType]);

  // Currency helpers
  const docCurrency = document.currency || 'ZAR';
  const currencyInfo = CURRENCY_CONFIG[docCurrency] || { symbol: docCurrency, flag: '' };

  // Fetch line items
  useEffect(() => {
    const fetchLineItems = async () => {
      setLoadingItems(true);
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;

        const response = await fetch(`/api/v1/work-items/${document.id}/bom`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data?.items) {
            setLineItems(result.data.items.map((item: any) => ({
              id: item.id,
              description: item.description || item.item_name || item.name || '',
              notes: item.notes || '',
              quantity: parseFloat(item.quantity) || 0,
              unit_price: parseFloat(item.unit_price) || parseFloat(item.unit_cost) || 0,
              unit_cost: parseFloat(item.unit_cost) || parseFloat(item.unit_price) || 0,
              unit: item.unit || item.unit_of_measure || 'each',
              tax_rate: parseFloat(item.tax_rate) || 0,
              tax_amount: parseFloat(item.tax_amount) || 0,
              discount_amount: parseFloat(item.discount_amount) || 0,
              line_total: parseFloat(item.line_total) || parseFloat(item.total) || 0,
              item_code: item.item_code || item.sku || '',
              line_type: item.line_type || 'item',
              is_optional: Boolean(item.is_optional),
              is_required: Boolean(item.is_required),
              quantity_editable: item.quantity_editable ?? true,
              price_editable: item.price_editable ?? true,
              source_bundle_name: item.source_bundle_name,
              purchase_supplier_mapping_id: item.purchase_supplier_mapping_id == null ? undefined : Number(item.purchase_supplier_mapping_id),
              purchase_supplier_contact_id: item.purchase_supplier_contact_id == null ? undefined : Number(item.purchase_supplier_contact_id),
              purchase_supplier_name: typeof item.purchase_supplier_name === 'string' ? item.purchase_supplier_name : null,
              purchase_supplier_sku: typeof item.purchase_supplier_sku === 'string' ? item.purchase_supplier_sku : null,
              purchase_supplier_currency: typeof item.purchase_supplier_currency === 'string' ? item.purchase_supplier_currency : null,
              purchase_supplier_cost_price: item.purchase_supplier_cost_price == null ? null : Number(item.purchase_supplier_cost_price),
              purchase_supplier_is_preferred: item.purchase_supplier_is_preferred === true,
              purchase_supplier_priority_rank: item.purchase_supplier_priority_rank == null ? null : Number(item.purchase_supplier_priority_rank),
              purchase_supplier_lead_time_days: item.purchase_supplier_lead_time_days == null ? null : Number(item.purchase_supplier_lead_time_days),
              purchase_supplier_selection_reason: typeof item.purchase_supplier_selection_reason === 'string' ? item.purchase_supplier_selection_reason : null,
              purchase_supplier_fallback_reason: typeof item.purchase_supplier_fallback_reason === 'string' ? item.purchase_supplier_fallback_reason : null,
              purchase_supplier_price_source: typeof item.purchase_supplier_price_source === 'string' ? item.purchase_supplier_price_source : null,
              purchase_supplier_matches_document_contact: item.purchase_supplier_matches_document_contact === true,
            })));
          }
        }
      } catch (error) {
        console.error('Failed to fetch line items:', error);
      } finally {
        setLoadingItems(false);
      }
    };

    if (document.id) {
      fetchLineItems();
    }
  }, [document.id]);

  const docTypeLabel = unifiedReportDocumentType
    ? DOCUMENT_TYPE_LABELS[unifiedReportDocumentType]
    : document.item_type;
  const paymentStatusColors = document.payment_status 
    ? (PAYMENT_STATUS_COLORS[document.payment_status] || PAYMENT_STATUS_COLORS.unpaid)
    : null;

  // Calculate if overdue
  const isOverdue = document.due_date && new Date(document.due_date) < new Date() && 
    document.payment_status !== 'paid';

  const purchaseSourcingAudit = useMemo(() => {
    if (!isPurchaseDocument) {
      return null;
    }

    const summaries = lineItems
      .map((item) => getPurchaseLineSourcingSummary(item, document.contact_id))
      .filter((summary): summary is NonNullable<ReturnType<typeof getPurchaseLineSourcingSummary>> => Boolean(summary));

    if (summaries.length === 0) {
      return null;
    }

    return {
      sourcedLines: summaries.length,
      matchedLines: summaries.filter((summary) => summary.matchesDocumentSupplier).length,
      mismatchLines: summaries.filter((summary) => summary.differsFromDocumentSupplier).length,
      preferredLines: summaries.filter((summary) => summary.isPreferred).length,
      varianceLines: summaries.filter((summary) => summary.hasVariance).length,
    };
  }, [document.contact_id, isPurchaseDocument, lineItems]);

  // Get accent color from branding
  const accentColor = clientBranding?.primary_color || '#3B82F6';

  return (
    <div className="space-y-5">
      {/* Premium Document Header Banner */}
      <div 
        className="rounded-xl overflow-hidden shadow-sm"
        style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)` }}
      >
        <div className="px-5 py-4 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-white/20 backdrop-blur rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-xl font-bold">{docTypeLabel}</h3>
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-white/20 backdrop-blur capitalize">
                    {document.status}
                  </span>
                  {paymentStatusColors && (
                    <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-white/20 backdrop-blur capitalize">
                      {document.payment_status}
                    </span>
                  )}
                  {isOverdue && (
                    <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-500/80 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Overdue
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <Hash className="w-3.5 h-3.5" />
                    {document.doc_number || document.item_number || 'Draft'}
                  </span>
                  {document.contact_name && (
                    <span className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {document.contact_name}
                    </span>
                  )}
                  {document.issued_at && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(document.issued_at)}
                    </span>
                  )}
                  {docCurrency !== 'ZAR' && (
                    <span className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      {currencyInfo.flag} {docCurrency}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {unifiedReportDocumentType && (
                <button
                  onClick={handleOpenPrintPreview}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 backdrop-blur transition-colors"
                  title="Open print preview"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              )}
              <button
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 backdrop-blur transition-colors disabled:opacity-50"
                title="Download PDF"
              >
                <Download className={`w-4 h-4 ${downloadingPDF ? 'animate-pulse' : ''}`} />
                {downloadingPDF ? 'Generating...' : 'PDF'}
              </button>
              {onEdit && (
                <button
                  onClick={onEdit}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 backdrop-blur transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              {onSend && (
                <button
                  onClick={onSend}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-white text-gray-800 hover:bg-gray-100 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              )}
              {onOpenFullModal && (
                <button
                  onClick={onOpenFullModal}
                  className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  title="Open full view"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expense Source Banner - Shows when bill was created from a field expense */}
      {document.custom_fields?.expense_source && (
        <div className={`rounded-lg border ${isDark ? 'border-amber-800/50 bg-amber-900/20' : 'border-amber-200 bg-amber-50'} p-4`}>
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-amber-900/30' : 'bg-amber-100'}`}>
              {document.custom_fields.expense_source.expense_type === 'fuel' ? (
                <Fuel className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              ) : document.custom_fields.expense_source.expense_type === 'toll' ? (
                <Receipt className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              ) : document.custom_fields.expense_source.expense_type === 'maintenance' ? (
                <Wrench className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              ) : (
                <Receipt className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              )}
            </div>
            <div className="flex-1">
              <h4 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                <Link2 className="w-4 h-4" />
                Created from Field Expense
              </h4>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {document.custom_fields.expense_source.expense_type && (
                  <div>
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Type</span>
                    <span className={`capitalize ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                      {document.custom_fields.expense_source.expense_type}
                    </span>
                  </div>
                )}
                {document.custom_fields.expense_source.vehicle_registration && (
                  <div>
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Vehicle</span>
                    <span className={`flex items-center gap-1 ${isDark ? 'text-amber-200' : 'text-amber-900'}`}>
                      <Car className="w-3 h-3" />
                      {document.custom_fields.expense_source.vehicle_registration}
                    </span>
                  </div>
                )}
                {document.custom_fields.expense_source.captured_by && (
                  <div>
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Captured By</span>
                    <span className={isDark ? 'text-amber-200' : 'text-amber-900'}>
                      {document.custom_fields.expense_source.captured_by}
                    </span>
                  </div>
                )}
                {document.custom_fields.expense_source.captured_at && (
                  <div>
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Captured</span>
                    <span className={isDark ? 'text-amber-200' : 'text-amber-900'}>
                      {formatDate(document.custom_fields.expense_source.captured_at)}
                    </span>
                  </div>
                )}
                {document.custom_fields.expense_source.odometer && (
                  <div>
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Odometer</span>
                    <span className={isDark ? 'text-amber-200' : 'text-amber-900'}>
                      {document.custom_fields.expense_source.odometer.toLocaleString()} km
                    </span>
                  </div>
                )}
                {document.custom_fields.expense_source.location && (
                  <div className="col-span-2">
                    <span className={`block text-xs ${isDark ? 'text-amber-500' : 'text-amber-600'}`}>Location</span>
                    <span className={isDark ? 'text-amber-200' : 'text-amber-900'}>
                      {document.custom_fields.expense_source.location}
                    </span>
                  </div>
                )}
              </div>
              {document.custom_fields.expense_source.receipt_image_url && (
                <div className="mt-3">
                  <a
                    href={document.custom_fields.expense_source.receipt_image_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-1.5 text-sm ${isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-700 hover:text-amber-800'}`}
                  >
                    <Camera className="w-4 h-4" />
                    View Receipt Photo
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Three Column Info Cards - matching DocumentModal style */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Contact Card */}
        <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'} p-4 shadow-sm`}>
          <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Building2 className="w-3.5 h-3.5" />
            {['supplier_invoice', 'purchase_order', 'grn', 'supplier_credit', 'supplier_quote'].includes(document.item_type) 
              ? 'Supplier' : 'Customer'}
          </h4>
          <div className="space-y-2">
            {document.contact_name && (
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{document.contact_name}</p>
            )}
            {document.contact_email && (
              <div className="flex items-center gap-2">
                <Mail className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <a href={`mailto:${document.contact_email}`} className="text-blue-600 hover:underline text-sm truncate">
                  {document.contact_email}
                </a>
              </div>
            )}
            {document.contact_phone && (
              <div className="flex items-center gap-2">
                <Phone className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{document.contact_phone}</span>
              </div>
            )}
            {!document.contact_name && !document.contact_email && (
              <p className={`text-sm italic ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No contact assigned</p>
            )}
          </div>
        </div>

        {/* Document Details Card */}
        <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'} p-4 shadow-sm`}>
          <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <FileText className="w-3.5 h-3.5" />
            Document Details
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Number</span>
              <span className={`font-mono font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {document.doc_number || document.item_number || '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Date</span>
              <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatDate(document.issued_at)}</span>
            </div>
            {document.due_date && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Due</span>
                <span className={`${isOverdue ? 'text-red-500 font-semibold' : (isDark ? 'text-white' : 'text-gray-900')}`}>
                  {formatDate(document.due_date)}
                </span>
              </div>
            )}
            {document.payment_terms && (
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Terms</span>
                <span className={isDark ? 'text-white' : 'text-gray-900'}>{document.payment_terms}</span>
              </div>
            )}
          </div>
        </div>

        {/* Amounts Card */}
        <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-white'} p-4 shadow-sm`}>
          <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-3 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <DollarSign className="w-3.5 h-3.5" />
            Amounts {docCurrency !== 'ZAR' && <span className="ml-1 text-[9px] font-normal">({docCurrency})</span>}
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Subtotal</span>
              <span className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatCurrency(document.subtotal, document.currency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Tax</span>
              <span className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatCurrency(document.tax_total, document.currency)}
              </span>
            </div>
            <div className={`flex justify-between pt-2 border-t ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
              <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
              <span className={`font-bold text-lg font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(document.grand_total, document.currency)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items - Enhanced with line_type support */}
      <div className={`rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'} overflow-hidden shadow-sm`}>
        <div className={`px-4 py-3 flex items-center justify-between ${isDark ? 'bg-gray-800' : 'bg-gray-50'} border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h4 className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Package className="w-3.5 h-3.5" />
            Line Items
          </h4>
          {lineItems.length > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
              {lineItems.filter(i => i.line_type === 'item' || !i.line_type).length} items
            </span>
          )}
        </div>

        {purchaseSourcingAudit && (
          <div className={`grid gap-3 sm:grid-cols-2 xl:grid-cols-5 px-4 py-4 border-b ${isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50/80 border-gray-200'}`}>
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Sourced Lines</div>
              <div className={`mt-1 text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{purchaseSourcingAudit.sourcedLines}</div>
            </div>
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Matches Supplier</div>
              <div className={`mt-1 text-lg font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>{purchaseSourcingAudit.matchedLines}</div>
            </div>
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Supplier Mismatch</div>
              <div className={`mt-1 text-lg font-semibold ${purchaseSourcingAudit.mismatchLines > 0 ? (isDark ? 'text-amber-300' : 'text-amber-700') : (isDark ? 'text-white' : 'text-gray-900')}`}>{purchaseSourcingAudit.mismatchLines}</div>
            </div>
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Preferred Source</div>
              <div className={`mt-1 text-lg font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>{purchaseSourcingAudit.preferredLines}</div>
            </div>
            <div>
              <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Price Variance</div>
              <div className={`mt-1 text-lg font-semibold ${purchaseSourcingAudit.varianceLines > 0 ? (isDark ? 'text-purple-300' : 'text-purple-700') : (isDark ? 'text-white' : 'text-gray-900')}`}>{purchaseSourcingAudit.varianceLines}</div>
            </div>
          </div>
        )}
        
        {loadingItems ? (
          <div className="p-10 text-center">
            <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
            <p className={`text-xs mt-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Loading items...</p>
          </div>
        ) : lineItems.length === 0 ? (
          <div className={`p-10 text-center ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
            <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No line items</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-[10px] font-bold uppercase tracking-wider ${isDark ? 'bg-gray-800/80 text-gray-500' : 'bg-gray-50 text-gray-500'}`}>
                  <th className="px-4 py-2.5">Description</th>
                  <th className="px-4 py-2.5 text-right w-20">Qty</th>
                  <th className="px-4 py-2.5 text-right w-28">Price</th>
                  <th className="px-4 py-2.5 text-right w-16">Tax</th>
                  <th className="px-4 py-2.5 text-right w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item) => {
                  const lt = item.line_type || 'item';
                  const quantityLocked = item.quantity_editable === false;
                  const priceLocked = item.price_editable === false;
                  const sourcing = getPurchaseLineSourcingSummary(item, document.contact_id);
                  
                  // Section Header row
                  if (lt === 'header') {
                    return (
                      <tr key={item.id} className={isDark ? 'bg-gray-800/40' : 'bg-gray-50/80'}>
                        <td colSpan={5} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {item.description || 'Section'}
                        </td>
                      </tr>
                    );
                  }
                  
                  // Comment/Note row
                  if (lt === 'comment') {
                    return (
                      <tr key={item.id}>
                        <td colSpan={5} className={`px-4 py-2 text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {item.description || ''}
                        </td>
                      </tr>
                    );
                  }
                  
                  // Subtotal row
                  if (lt === 'subtotal') {
                    return (
                      <tr key={item.id} className={`border-t-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                        <td colSpan={4} className={`px-4 py-2.5 text-right text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.description || 'Subtotal'}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-bold font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {formatCurrency(item.line_total, document.currency)}
                        </td>
                      </tr>
                    );
                  }
                  
                  // Spacer row
                  if (lt === 'spacer') {
                    return (
                      <tr key={item.id}>
                        <td colSpan={5} className="py-2">
                          <div className={`border-t ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`} />
                        </td>
                      </tr>
                    );
                  }
                  
                  // Regular item row
                  return (
                    <tr 
                      key={item.id} 
                      className={`transition-colors ${
                        isDark 
                          ? 'hover:bg-gray-800/30 border-b border-gray-700/50' 
                          : 'hover:bg-blue-50/30 border-b border-gray-100'
                      } ${item.source_bundle_name ? (isDark ? 'bg-purple-950/20' : 'bg-purple-50/40') : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="space-y-1.5">
                          <div>
                            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.description}</span>
                            {item.item_code && (
                              <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-mono ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                {item.item_code}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {item.source_bundle_name && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                              }`}>
                                SLA · {item.source_bundle_name}
                              </span>
                            )}
                            {item.is_required && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                Required
                              </span>
                            )}
                            {item.is_optional && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'
                              }`}>
                                Optional
                              </span>
                            )}
                            {quantityLocked && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                              }`}>
                                Qty locked
                              </span>
                            )}
                            {priceLocked && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isDark ? 'bg-fuchsia-900/30 text-fuchsia-300' : 'bg-fuchsia-100 text-fuchsia-700'
                              }`}>
                                Price locked
                              </span>
                            )}
                            {sourcing && (
                              <>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                  sourcing.matchesDocumentSupplier
                                    ? (isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                                    : (isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700')
                                }`}>
                                  {sourcing.priceSourceLabel}
                                </span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                  isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
                                }`}>
                                  {sourcing.supplierName}
                                </span>
                                {sourcing.priorityRank != null && !sourcing.isPreferred && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    Rank #{sourcing.priorityRank}
                                  </span>
                                )}
                                {sourcing.leadTimeDays != null && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    Lead {sourcing.leadTimeDays}d
                                  </span>
                                )}
                                {sourcing.supplierSku && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    isDark ? 'bg-cyan-900/30 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
                                  }`}>
                                    SKU {sourcing.supplierSku}
                                  </span>
                                )}
                                {sourcing.differsFromDocumentSupplier && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    isDark ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-100 text-amber-700'
                                  }`}>
                                    Different to document supplier
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {sourcing?.reason && (
                            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {sourcing.reason}
                            </div>
                          )}
                          {item.notes && (
                            <div className={`text-xs italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.notes}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {item.quantity}{item.unit ? ` ${item.unit}` : ''}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <div className="space-y-1">
                          <div>{formatCurrency(item.unit_price, document.currency)}</div>
                          {sourcing?.supplierCostPrice != null && (
                            <div className={`text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Mapped {formatCurrency(sourcing.supplierCostPrice, document.currency)}
                            </div>
                          )}
                          {sourcing?.hasVariance && sourcing.varianceAmount != null && (
                            <div className={`text-[11px] font-medium ${sourcing.varianceAmount > 0
                              ? (isDark ? 'text-amber-300' : 'text-amber-700')
                              : (isDark ? 'text-emerald-300' : 'text-emerald-700')}`}>
                              {sourcing.varianceAmount > 0 ? '+' : ''}{formatCurrency(sourcing.varianceAmount, document.currency)}
                              {sourcing.variancePercent != null ? ` · ${sourcing.variancePercent > 0 ? '+' : ''}${sourcing.variancePercent}%` : ''}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {item.tax_rate ? `${item.tax_rate}%` : '-'}
                      </td>
                      <td className={`px-4 py-3 text-right font-mono font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(item.line_total, document.currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Financial Summary - Premium card */}
      <div className={`rounded-xl border-2 ${isDark ? 'border-gray-600 bg-gradient-to-br from-gray-800 to-gray-800/60' : 'border-gray-200 bg-gradient-to-br from-white to-gray-50/80'} p-5 shadow-sm`}>
        <div className="flex justify-end">
          <div className="w-full max-w-sm space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Subtotal</span>
              <span className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatCurrency(document.subtotal, document.currency)}
              </span>
            </div>
            {(document.discount_amount && document.discount_amount > 0) && (
              <div className="flex justify-between text-sm">
                <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>Discount</span>
                <span className="text-green-500 font-mono">
                  -{formatCurrency(document.discount_amount, document.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className={isDark ? 'text-gray-500' : 'text-gray-500'}>VAT / Tax</span>
              <span className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatCurrency(document.tax_total, document.currency)}
              </span>
            </div>
            
            <div className={`pt-3 mt-1 border-t-2 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
              <div className="flex justify-between items-baseline">
                <span className={`text-sm font-bold uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total</span>
                <span className={`text-2xl font-bold font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(document.grand_total, document.currency)}
                </span>
              </div>
              {docCurrency !== 'ZAR' && currencyInfo && document.exchange_rate && (
                <div className="flex justify-end mt-1">
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    ≈ {formatCurrency((document.grand_total || 0) * (document.exchange_rate || 1), 'ZAR')} ZAR
                  </span>
                </div>
              )}
            </div>
            
            {/* Payment Info for invoices/bills */}
            {['invoice', 'supplier_invoice'].includes(document.item_type) && (
              <div className={`mt-3 pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} space-y-2`}>
                {(document.amount_paid && document.amount_paid > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Paid</span>
                    </span>
                    <span className="text-green-500 font-mono font-medium">
                      {formatCurrency(document.amount_paid, document.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-baseline">
                  <span className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${
                      (document.balance_due || 0) > 0 
                        ? (isOverdue ? 'bg-red-500' : 'bg-amber-500') 
                        : 'bg-green-500'
                    }`}></div>
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Balance Due</span>
                  </span>
                  <span className={`text-lg font-bold font-mono ${
                    (document.balance_due || 0) > 0 
                      ? (isOverdue ? 'text-red-500' : 'text-amber-600') 
                      : 'text-green-600'
                  }`}>
                    {formatCurrency(document.balance_due, document.currency)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes & Terms - Side by side when both exist */}
      {(document.notes || document.terms_and_conditions) && (
        <div className={`grid ${document.notes && document.terms_and_conditions ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-4`}>
          {document.notes && (
            <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'} p-4 shadow-sm`}>
              <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-2.5 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <FileText className="w-3.5 h-3.5" />
                Notes
              </h4>
              <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {document.notes}
              </p>
            </div>
          )}
          {document.terms_and_conditions && (
            <div className={`rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-white'} p-4 shadow-sm`}>
              <h4 className={`text-[10px] font-bold uppercase tracking-wider mb-2.5 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Shield className="w-3.5 h-3.5" />
                Terms &amp; Conditions
              </h4>
              <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                {document.terms_and_conditions}
              </p>
            </div>
          )}
        </div>
      )}

      {unifiedReportDocumentType && (
        <UnifiedDocumentPreviewModal
          documentId={document.id}
          documentType={unifiedReportDocumentType}
          documentNumber={document.doc_number || document.item_number || document.reference_number || null}
          isOpen={showUnifiedPreview}
          onClose={() => setShowUnifiedPreview(false)}
          title={`${docTypeLabel} Preview`}
        />
      )}
    </div>
  );
}
